import { useState, useEffect  } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const agencyTypeIcon: Record<string, string> = { barangay: 'ri-community-line', police: 'ri-shield-star-line', school: 'ri-book-open-line', hospital: 'ri-hospital-line', ngo: 'ri-heart-line', court: 'ri-scales-3-line', other: 'ri-building-line' };
type AgencyType =
  | 'barangay'
  | 'police'
  | 'school'
  | 'hospital'
  | 'ngo'
  | 'court'
  | 'other';

type CoordinationForm = {
  caseNumber: string;
  childName: string;
  agency: string;
  agencyType: AgencyType;
  contactPerson: string;
  contactNumber: string;
  purpose: string;
  outcome: string;
}; 
  

export default function SWCoordinationPage() {
  const { user } = useAuth();
  const [coordLogs, setCoordLogs] = useState<any[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const [form, setForm] = useState<CoordinationForm>({
  caseNumber: '',
  childName: '',
  agency: '',
  agencyType: 'barangay',
  contactPerson: '',
  contactNumber: '',
  purpose: '',
  outcome: ''
});


  useEffect(() => {

  if (!user?.uid) return;

  const fetchData = async () => {

    try {

      /* =========================
         COORDINATION
      ========================= */

      const coordRes = await fetch(

        `https://guardiancloudcare.com/backend/api/get_coordination.php?uid=${user.uid}`

      );

      const coordText = await coordRes.text();

      console.log(
        "COORDINATION API:",
        coordText
      );

      const coordData =
        JSON.parse(coordText);

      if (Array.isArray(coordData)) {

        setCoordLogs(coordData);

      } else {

        setCoordLogs([]);

      }

      /* =========================
         CHILDREN
      ========================= */

      const childRes = await fetch(

        `https://guardiancloudcare.com/backend/api/get_children.php?uid=${user.uid}`

      );

      const childText = await childRes.text();

      console.log(
        "CHILDREN API:",
        childText
      );

      const childData =
        JSON.parse(childText);

      if (Array.isArray(childData)) {

        setCases(childData);

      } else {

        setCases([]);

      }

    } catch (err) {

      console.error(err);

      setCoordLogs([]);

      setCases([]);

    }

  };

  fetchData();

}, [user?.uid]);
  
  const handleAdd = async () => {
    if (
      !form.caseNumber ||
      !form.agency ||
      !form.contactPerson ||
      !form.contactNumber ||
      !form.purpose
    ) {
      alert("Please fill all required fields");
      return;
    }

    await fetch("https://guardiancloudcare.com/backend/api/add_coordination.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        case_number: form.caseNumber,
        child_name: form.childName,
        agency: form.agency,
        agency_type: form.agencyType,
        contact_person: form.contactPerson,
        contact_number: form.contactNumber,
        purpose: form.purpose,
        outcome: form.outcome,
        logged_by: user?.name,
        uid: user?.uid
      })
    });

    setShowModal(false);

    fetch(`https://guardiancloudcare.com/backend/api/get_coordination.php?uid=${user?.uid}`)
      .then(res => res.json())
      .then(data => setCoordLogs(data));

    // reset form
    setForm({
      caseNumber: '',
      childName: '',
      agency: '',
      agencyType: 'barangay',
      contactPerson: '',
      contactNumber: '',
      purpose: '',
      outcome: ''
    });
  };

  const filteredLogs = coordLogs.filter((log) => {
  const keyword = search.toLowerCase();

    return (
      log.case_number?.toLowerCase().includes(keyword) ||
      log.full_name?.toLowerCase().includes(keyword) ||
      log.child_name?.toLowerCase().includes(keyword)
    );
  });

  return (
    <AppLayout title="Coordination Logs" breadcrumb={['Social Worker', 'Coordination']}>
      <div className="flex justify-end mb-5">
        <button onClick={() => setShowModal(true)} className="bg-teal-500 text-white text-sm px-5 py-2.5 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-add-line" />Log Coordination</button>
      </div>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by child name or case number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-teal-500"
        />
      </div>
      <div className="space-y-3">
        {filteredLogs.map((log) => (
          <div key={log.id} className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-start gap-4">

              <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className={`${agencyTypeIcon[log.agency_type]} text-teal-600 text-lg`} />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{log.agency}</p>

                    {/* ✅ FIXED */}
                    <p className="text-xs text-gray-500">
                      Contact: {log.contact_person} · {log.contact_number}
                    </p>
                  </div>

                  <div className="text-right flex-shrink-0">
                    {/* ✅ FIXED */}
                    <p className="text-xs font-mono text-gray-400">
                      {log.case_number}
                    </p>

                    <p 
                      onClick={() => navigate(`/child-profiles/${log.case_number}`)}
                      className="text-xs text-teal-600 cursor-pointer hover:underline"
                    >
                      {log.full_name || log.child_name}
                    </p>
                    <p className="text-xs text-gray-400">{log.date}</p>
                  </div>
                </div>

                <p className="text-sm text-gray-700 mb-1">
                  <strong>Purpose:</strong> {log.purpose}
                </p>

                {log.outcome && (
                  <p className="text-sm text-gray-600">
                    <strong>Outcome:</strong> {log.outcome}
                  </p>
                )}

                {/* ✅ FIXED */}
                <p className="text-xs text-gray-400 mt-1">
                  Logged by: {log.logged_by}
                </p>
              </div>
            </div>
          </div>
        ))}

        {filteredLogs.length === 0 && (
          <div className="text-center text-gray-400 py-10">
            No matching records found
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Log Coordination</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="space-y-4">
              <select
                value={form.caseNumber}
                onChange={(e) => {
                  const selected = cases.find(c => c.case_number === e.target.value);

                  if (!selected) return; 

                  setForm({
                    ...form,
                    caseNumber: selected.case_number,
                    childName: selected.full_name
                  });
                }}
              >
                <option value="">Select case...</option>
                {cases.map(c => (
                  <option key={c.case_number} value={c.case_number}>
                    {c.case_number} — {c.full_name}
                  </option>
                ))}
              </select>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Agency / Organization <span className="text-red-500">*</span></label>
                <input value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} placeholder="e.g. PNP Batangas City" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500" /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Agency Type</label>
                <select
                  value={form.agencyType} 
                  onChange={(e) => setForm({ 
                    ...form, 
                    agencyType: e.target.value as AgencyType
                  })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 cursor-pointer"
                >
                  <option value="barangay">Barangay</option>
                  <option value="police">Police</option>
                  <option value="school">School</option>
                  <option value="hospital">Hospital / Health</option>
                  <option value="ngo">NGO</option>
                  <option value="court">Court / Legal</option>
                  <option value="other">Other</option>
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Contact Person</label>
                  <input value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} placeholder="Name" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500" /></div>
                <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Contact Number</label>
                  <input value={form.contactNumber} onChange={(e) => setForm({ ...form, contactNumber: e.target.value })} placeholder="09XX-XXX-XXXX" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500" /></div>
              </div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Purpose <span className="text-red-500">*</span></label>
                <textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} rows={2} maxLength={300} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Outcome / Result</label>
                <textarea value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} rows={2} maxLength={300} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" /></div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowModal(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancel</button>
              <button onClick={handleAdd} className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap">Log Coordination</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
